/**
 * Base URL for API requests
 */
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Debug API configuration
 */
const debugApiConfig = () => {
  console.log('🔧 API Configuration Debug:');
  console.log('  API_BASE_URL:', API_BASE_URL);
  console.log('  NODE_ENV:', process.env.NODE_ENV);
  console.log('  REACT_APP_ENV:', process.env.REACT_APP_ENV);
  console.log('  isStaging():', isStaging());
  console.log('  Current Origin:', window.location.origin);
  console.log('  Current Host:', window.location.host);
};

/**
 * Check if we're in staging environment (using ngrok)
 */
const isStaging = (): boolean => {
  return process.env.NODE_ENV === 'development' && 
         (API_BASE_URL?.includes('ngrok') || 
          process.env.REACT_APP_ENV === 'staging');
};

/**
 * Get common headers for API requests
 */
const getCommonHeaders = (token?: string | null, includeAuth: boolean = true): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available and requested
  if (token && includeAuth) {
    headers['Authorization'] = `Token ${token}`;
  }
  console.log('🔍 Environment check:', {
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    NODE_ENV: process.env.NODE_ENV,
    isStaging: isStaging(),
    API_BASE_URL: API_BASE_URL
  });
  
  // Add ngrok header for development environments (already covered by isStaging)
  if (isStaging()) {
    headers['ngrok-skip-browser-warning'] = '69420';
    console.log('✅ Added ngrok header');
  }

  return headers;
};

/**
 * Build a full API URL from an endpoint path
 * @param endpoint API endpoint path
 * @returns Full API URL
 */
export const buildApiUrl = (endpoint: string): string => {
  // Ensure endpoint starts with a slash
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${formattedEndpoint}`;
};

/**
 * Save token to storage (local or session)
 * @param token Auth token
 * @param rememberMe Whether to store in localStorage (true) or sessionStorage (false)
 */
export const saveAuthToken = (token: string, rememberMe = false): void => {
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem('token', token);
};

/**
 * Clear auth token and user data from storage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  sessionStorage.removeItem('isLoggedIn');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('token');
};

/**
 * API client for making authenticated requests
 */

// Function to get the auth token from storage
export const getAuthToken = (): string | null => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// Error handling for API responses
export const processApiError = async (response: Response): Promise<never> => {
  try {
    const errorData = await response.json();
    
    // Handle validation errors (typically 400 or 422 status codes)
    if (response.status === 400 || response.status === 422) {
      let errorMessages = '';
      
      // Check if the error data has field-specific validation errors
      if (typeof errorData === 'object' && errorData !== null) {
        Object.entries(errorData).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errorMessages += `${field}: ${errors.join(', ')}\n`;
          } else if (typeof errors === 'string') {
            errorMessages += `${field}: ${errors}\n`;
          }
        });
      }
      
      throw new Error(errorMessages || errorData.error || `Validation failed: ${response.status}`);
    }
    
    // Handle other types of errors
    if (errorData.error || errorData.detail) {
      throw new Error(errorData.error || errorData.detail);
    }
    
    throw new Error(`API request failed with status: ${response.status}`);
  } catch (parseError) {
    // If we can't parse the error response as JSON
    throw new Error(`API request failed: ${response.statusText || response.status}`);
  }
};

// API client for all requests
export const apiClient = {
  /**
   * Make a GET request
   * @param endpoint API endpoint
   * @returns Promise with response data
   */
  get: async <T = any>(endpoint: string): Promise<T> => {
    const token = getAuthToken();
    
    // Log invoices requests to track multiple calls with timestamp
    if (endpoint.includes('/billing/invoices/')) {
      const timestamp = new Date().toISOString();
      console.log(`🔍 [${timestamp}] API GET request to invoices endpoint:`, endpoint);
      console.trace('Call stack for invoices endpoint');
    }
    
    // Log MercadoPago credentials requests
    if (endpoint.includes('/payment/oauth/mercadopago/credentials/')) {
      console.log('🔍 API GET request to MercadoPago credentials endpoint:', endpoint);
    }
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getCommonHeaders(token),
    });
    
    if (!response.ok) {
      return processApiError(response);
    }
    
    const responseData = await response.json();
    
    // Log MercadoPago credentials responses
    if (endpoint.includes('/payment/oauth/mercadopago/credentials/')) {
      console.log('📋 API GET response from MercadoPago credentials:', responseData);
    }
    
    return responseData;
  },

  /**
   * Make a GET request without authentication
   * @param endpoint API endpoint
   * @returns Promise with response data
   */
  getPublic: async <T = any>(endpoint: string): Promise<T> => {
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getCommonHeaders(null, false),
    });
    
    if (!response.ok) {
      return processApiError(response);
    }
    
    return response.json();
  },
  
  /**
   * Make a POST request
   * @param endpoint API endpoint
   * @param data Request data
   * @returns Promise with response data
   */
  post: async <T = any>(endpoint: string, data: any): Promise<T> => {
    const token = getAuthToken();
    
    // Enhanced debugging for API calls
    if (endpoint.includes('/billing/invoices/')) {
      console.log('API POST REQUEST TO:', endpoint);
      console.log('DATA BEING SENT:', JSON.stringify(data, null, 2));
      console.log('TAX ID DATA CHECK:', {
        debtor_tax_id_type: data.debtor_tax_id_type,
        debtor_tax_id_number: data.debtor_tax_id_number
      });
    }
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'POST',
      headers: getCommonHeaders(token),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      console.log('API POST ERROR RESPONSE:', response.status, response.statusText);
      return processApiError(response);
    }
    
    const responseData = await response.json();
    if (endpoint.includes('/billing/invoices/')) {
      console.log('API POST SUCCESS RESPONSE:', responseData);
    }
    return responseData;
  },

  /**
   * Make a POST request without authentication (for login, register, etc.)
   * @param endpoint API endpoint
   * @param data Request data
   * @returns Promise with response data
   */
  postPublic: async <T = any>(endpoint: string, data: any): Promise<T> => {
    const headers = getCommonHeaders(null, false);
    console.log('📤 postPublic headers:', headers);
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return processApiError(response);
    }
    
    return response.json();
  },
  
  /**
   * Make a PUT request
   * @param endpoint API endpoint
   * @param data Request data
   * @returns Promise with response data
   */
  put: async <T = any>(endpoint: string, data: any): Promise<T> => {
    const token = getAuthToken();
    
    // Enhanced debugging for API calls
    if (endpoint.includes('/billing/invoices/')) {
      console.log('API PUT REQUEST TO:', endpoint);
      console.log('DATA BEING SENT:', JSON.stringify(data, null, 2));
      console.log('TAX ID DATA CHECK:', {
        debtor_tax_id_type: data.debtor_tax_id_type,
        debtor_tax_id_number: data.debtor_tax_id_number
      });
    }
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'PUT',
      headers: getCommonHeaders(token),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      console.log('API PUT ERROR RESPONSE:', response.status, response.statusText);
      return processApiError(response);
    }
    
    const responseData = await response.json();
    if (endpoint.includes('/billing/invoices/')) {
      console.log('API PUT SUCCESS RESPONSE:', responseData);
    }
    return responseData;
  },
  
  /**
   * Make a DELETE request
   * @param endpoint API endpoint
   * @returns Promise with response data
   */
  delete: async <T = any>(endpoint: string): Promise<T> => {
    const token = getAuthToken();
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'DELETE',
      headers: getCommonHeaders(token),
    });
    
    if (!response.ok) {
      return processApiError(response);
    }
    
    return response.json();
  },
  
  /**
   * Make a PATCH request
   * @param endpoint API endpoint
   * @param data Request data
   * @returns Promise with response data
   */
  patch: async <T = any>(endpoint: string, data: any): Promise<T> => {
    const token = getAuthToken();
    
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'PATCH',
      headers: getCommonHeaders(token),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return processApiError(response);
    }
    
    return response.json();
  },
};
